import {
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import type {
  Anchor,
  BoardConnection,
  BoardElement,
  DisplayMode,
  End,
  OpenBoard,
  Place,
} from '../../api/boards'
import { Connections } from './Connections'
import { type Entry, Menu } from './Menu'
import { type Corner, Element } from './Element'
import {
  ANCHOR_WITHIN,
  type Handle,
  REMOVED_WITHIN,
  anchorAt,
  anchorPoint,
  cornersOf,
  elementAt,
  endsOf,
  rectOf,
  waypointsAfterDragging,
  waypointsMoved,
  waypointsWith,
  waypointsWithout,
} from './routing'
import {
  AT_REST,
  type Point,
  type Rect,
  toScreen,
  type Viewport,
  boundsOf,
  bringIntoView,
  clampScale,
  fitAll,
  holds,
  pannedBy,
  toBoard,
  zoomAround,
  zoomTo,
} from './viewport'

/**
 * The surface itself: one transformed layer, elements placed absolutely inside it, and the lines
 * drawn over them.
 *
 * **Built rather than taken off a shelf**, and the reason is what was ruled out: freehand drawing.
 * With that gone, what is left is rectangles at coordinates — and every drawing library would bring
 * its own way of being operated, which would then have to be argued with about direct manipulation,
 * about gestures never being the only way, and about what a surface should feel like. It would also
 * be the first runtime dependency in this frontend that has nothing to do with writing. The price is
 * that panning, zooming, selecting, resizing and routing are handwritten, touch included, and that
 * is the largest single piece of this area.
 *
 * ## What the pointer does
 *
 * One handler and one gesture at a time, held in a ref rather than in state — a drag produces
 * pointer moves faster than React re-renders, and a gesture that lived in state would be a render
 * per move of the pointer. What is drawn *during* a gesture is the one thing that is state, and it
 * is one map of overridden rectangles.
 *
 * - **on the empty surface** — dragging moves the board, and letting go on it clears the selection.
 * - **on an element** — dragging moves it, and everything else that is selected with it. A *frame*
 *   takes what lies inside it, worked out at the moment the drag begins.
 * - **two fingers** — pinch to zoom, about the point between them.
 * - **the wheel** — scrolls the board; with the modifier key, or a trackpad pinch, it zooms.
 *
 * Every one of those has a visible control beside it in the toolbar. That is not a fallback for
 * touch; it is the rule that nothing in MyLib may be reachable by gesture alone.
 *
 * ## What it does not do on a phone
 *
 * Rearranging. `rearrangeable` is `false` there, and the board is then something to look at and walk
 * over: it pans, it zooms, cards open, the search finds. That is a decision and not a gap — arranging
 * things spatially on a screen the size of a hand is a worse version of doing it on an iPad.
 */
export interface CanvasHandle {
  /** Brings everything into the pane at once. */
  readonly fitAll: () => void
  /** Puts one element in the middle, keeping the zoom — what a jump to a hit does. */
  readonly jumpTo: (element: string) => void
  readonly zoomBy: (factor: number) => void
  readonly zoomTo: (scale: number) => void
  /** The middle of what is on the screen, in board coordinates — where something new is put down. */
  readonly middle: () => Point
  /** Opens a sticky note or a text block for writing, from somewhere outside the board. */
  readonly write: (element: string) => void
}

export interface CanvasProps {
  readonly board: OpenBoard
  readonly rearrangeable: boolean
  readonly selection: ReadonlySet<string>
  readonly onSelection: (ids: ReadonlySet<string>) => void
  readonly selectedLine: string | null
  readonly onSelectLine: (line: string | null) => void
  /** Held highlighted after a jump, until something else is looked at. */
  readonly marked: string | null
  /** A line is being drawn: the next two things pointed at become its ends. */
  readonly connecting: boolean
  readonly onConnected: (from: End, to: End) => void
  /** A move or a resize, finished. The second argument is where things were, so it can be undone. */
  readonly onMoved: (
    places: readonly { readonly id: string; readonly place: Place }[],
    before: readonly { readonly id: string; readonly place: Place }[],
  ) => void
  readonly onOpen: (element: BoardElement) => void
  readonly onWrite: (element: BoardElement, text: string) => Promise<void>
  readonly onMarkup: (element: BoardElement, format: string) => Promise<void>
  readonly onDisplayPage: (element: BoardElement, page: number) => void
  /** A card switched between reading and writing a document. Reading state, so it is not undoable. */
  readonly onDisplayMode: (element: BoardElement, mode: DisplayMode) => void
  readonly onLineChanged: (line: BoardConnection, waypoints: readonly Point[]) => void
  /** One end of a line was taken somewhere else — onto something, or onto the bare surface. */
  readonly onEndMoved: (line: BoardConnection, which: 'from' | 'to', end: End) => void
  /** What the right-click menu offers, for the thing it was asked about — or for the bare surface. */
  readonly onMenu: (on: BoardElement | null) => readonly Entry[]
  readonly handle?: Ref<CanvasHandle>
}

/** What a gesture in progress is. Held in a ref: a drag outruns rendering. */
type Gesture =
  | { kind: 'pan'; pointer: number; from: Point; viewport: Viewport }
  | { kind: 'move'; pointer: number; from: Point; original: Map<string, Place> }
  | { kind: 'resize'; pointer: number; from: Point; corner: Corner; id: string; original: Place }
  | { kind: 'waypoint'; pointer: number; from: Point; line: BoardConnection; handle: Handle }
  | { kind: 'connect'; pointer: number; from: End; at: Point }
  | null

/** Where the viewport of each board is remembered, so coming back lands where you left. */
const VIEWPORT_KEY = 'mylib.desk.viewport'

export function Canvas({
  board,
  rearrangeable,
  selection,
  onSelection,
  selectedLine,
  onSelectLine,
  marked,
  connecting,
  onConnected,
  onMoved,
  onOpen,
  onWrite,
  onMarkup,
  onDisplayPage,
  onDisplayMode,
  onLineChanged,
  onEndMoved,
  onMenu,
  handle,
}: CanvasProps) {
  const pane = useRef<HTMLDivElement>(null)
  const [viewport, setViewport] = useState<Viewport>(() => remembered(board.board.id))
  /** What is being dragged right now, drawn instead of what the service last said. */
  const [moving, setMoving] = useState<ReadonlyMap<string, Place> | null>(null)
  const [drawing, setDrawing] = useState<{ from: Point; to: Point } | null>(null)
  /**
   * The line being bent right now, drawn instead of what the service last said.
   *
   * The same arrangement as [moving] for elements, and for the same reason: a corner has to follow
   * the pointer, and what follows it has to be the *line*, re-routed, rather than a dashed thread
   * laid over the old one.
   */
  const [bending, setBending] = useState<{ id: string; waypoints: Point[] } | null>(null)
  /**
   * Whether one end of a line is being aimed right now.
   *
   * Separate from `connecting`, and it has to be: the anchor points on the elements were shown only
   * in that mode, so dragging the end of a line that already existed left nothing to aim at. Aiming
   * is aiming, whether the line is being drawn for the first time or moved.
   */
  const [aiming, setAiming] = useState(false)
  /**
   * Where the end being aimed would land if it were let go of now.
   *
   * Shown on the board while the drag lasts. Without it the rule is invisible: whether a release
   * takes an anchor or leaves it to the line is decided by arithmetic the person doing it cannot
   * see, so aiming becomes guessing and then feels like missing.
   */
  const [aimedAt, setAimedAt] = useState<End | null>(null)
  /**
   * Which sticky note or text block is open for writing.
   *
   * Here rather than inside the element, because a touch pair is decided by something only the board
   * knows: whether a press *became a drag*. A mouse uses the element's ordinary `dblclick`, after the
   * browser has finished dispatching both clicks.
   */
  const [writing, setWriting] = useState<string | null>(null)
  /** Where the right-click menu stands, and what it was asked about. */
  const [menu, setMenu] = useState<{ at: Point; on: BoardElement | null } | null>(null)

  /**
   * The board as it stood when a drag was let go of, while the service is being told about it.
   *
   * It covers a moved card and a bent line alike — both are drawn from an overlay while the gesture
   * lasts, and both would blink back to where they came from without this.
   *
   * **This is what stops a card blinking back to where it came from.** The overlay used to be dropped
   * the moment the pointer came up, and until the board had been read again the card was drawn from
   * what the service last said — its *old* place, for one frame. So the overlay now outlives the
   * gesture and is let go of when the board itself has changed, which is the moment the new places
   * are the ones being drawn.
   *
   * State and not a ref, because the render is what reads it — and a ref read while rendering is a
   * value React may see stale.
   */
  const [settling, setSettling] = useState<OpenBoard | null>(null)

  const gesture = useRef<Gesture>(null)
  /**
   * Whether the pointer has moved far enough for the press to have become a drag.
   *
   * **This is why the capture is not taken on the press.** `setPointerCapture` retargets every later
   * event for that pointer at the pane, and the browser dispatches `click` at the common ancestor of
   * where the pointer went down and where it came up — so capturing straight away meant no card, no
   * sticky note and no line ever received a click at all. Now the press only *arms* a gesture; the
   * first move past [DRAG_BEGINS_AT] takes the capture and starts it, and a press that never moves
   * stays a click.
   */
  const dragging = useRef(false)
  /** What the current press landed on, and which kind of pointer made it. */
  const pressed = useRef<{ on: string; pointerType: string } | null>(null)
  /**
   * The last press that stayed a press: what it landed on, and when.
   *
   * The touch way to read "twice on the same thing", which browsers do not consistently turn into a
   * `dblclick` on a surface. A mouse is deliberately not handled here: changing the element into an
   * editor during the second `pointerup` replaces the target before that click and its `dblclick`
   * have finished, which makes the editor appear for a moment and close again.
   */
  const tapped = useRef<{ on: string; at: number } | null>(null)
  const pointers = useRef(new Map<number, Point>())
  const pinch = useRef<{ distance: number; middle: Point } | null>(null)
  /**
   * The board and the viewport, where something outside React's rendering has to read them.
   *
   * Filled after each render rather than during one: a ref written while rendering is a value React
   * may see twice or not at all, and this one is read from a handle somebody else holds.
   */
  const latest = useRef({ board, viewport })
  useEffect(() => {
    latest.current = { board, viewport }
  })

  // The answer has come back — from the save, or from a save that failed and was read again anyway.
  // Whichever it was, what is on the screen is the board's own again.
  if (settling !== null && settling !== board) {
    setSettling(null)
    setMoving(null)
    setBending(null)
  }

  const placed = board.elements.map((element) => withPlace(element, moving?.get(element.id)))
  /*
   * Built from the elements **as they are being drawn**, overlay and all — not from what the service
   * last said. A line hangs off a rectangle, so a map of the old rectangles meant the line stood
   * still until the card was let go of and only then jumped after it.
   */
  const elements = new Map(placed.map((element) => [element.id, element]))

  useEffect(() => {
    remember(board.board.id, viewport)
  }, [board.board.id, viewport])

  /*
   * Keep a touch gesture on the board instead of letting Safari scroll the page with it.
   *
   * `touch-action: none` says the same thing in CSS on the pane and every element, and that is the
   * standard path. Safari on iPad nevertheless starts native scrolling when the touch begins over a
   * nested scrolling control such as the preview inside a sticky note. Once that scroll has started,
   * the pointer stream is cancelled and the card can no longer follow the finger. A native,
   * explicitly non-passive listener is the fallback Safari needs: React's delegated touch listener
   * may be passive, where `preventDefault()` is deliberately ignored.
   */
  useEffect(() => {
    const node = pane.current
    if (node === null) {
      return
    }
    const keepOnBoard = (event: TouchEvent) => {
      event.preventDefault()
    }
    node.addEventListener('touchmove', keepOnBoard, { passive: false })
    return () => {
      node.removeEventListener('touchmove', keepOnBoard)
    }
  }, [])

  const paneSize = useCallback(() => {
    const node = pane.current
    return node === null
      ? { width: 0, height: 0 }
      : { width: node.clientWidth, height: node.clientHeight }
  }, [])

  const at = useCallback((event: { clientX: number; clientY: number }): Point => {
    const box = pane.current?.getBoundingClientRect()
    return box === undefined
      ? { x: event.clientX, y: event.clientY }
      : { x: event.clientX - box.left, y: event.clientY - box.top }
  }, [])

  /**
   * Opens the words of a sticky note or a text block, whoever asked.
   *
   * Read out of [latest] rather than out of the render's own `board`, because two of the three ways
   * in reach this from outside a render — the imperative handle and the end of a gesture — and both
   * would otherwise decide on a board that has since changed.
   */
  const openWriting = useCallback((id: string) => {
    const element = latest.current.board.elements.find((one) => one.id === id)
    if (
      element !== undefined &&
      !element.locked &&
      (element.kind === 'sticky' || element.kind === 'text')
    ) {
      setWriting(id)
    }
  }, [])

  useImperativeHandle(
    handle,
    () => ({
      fitAll: () => {
        setViewport(fitAll(everythingOn(latest.current.board), paneSize()))
      },
      jumpTo: (id: string) => {
        const element = latest.current.board.elements.find((one) => one.id === id)
        if (element !== undefined) {
          setViewport((current) => bringIntoView(current, rectOf(element), paneSize()))
        }
      },
      zoomBy: (factor: number) => {
        const size = paneSize()
        setViewport((current) =>
          zoomAround(current, { x: size.width / 2, y: size.height / 2 }, factor),
        )
      },
      zoomTo: (scale: number) => {
        setViewport((current) => zoomTo(current, paneSize(), scale))
      },
      middle: () => {
        const size = paneSize()
        return toBoard(latest.current.viewport, { x: size.width / 2, y: size.height / 2 })
      },
      write: (id: string) => {
        openWriting(id)
      },
    }),
    [paneSize, openWriting],
  )

  // A jump to an element, from a search hit or from the board's own find. Only when the address
  // changes: re-running it on every render would fight whoever is panning.
  useEffect(() => {
    if (marked === null) {
      return
    }
    const element = board.elements.find((one) => one.id === marked)
    if (element === undefined) {
      return
    }
    const node = pane.current
    setViewport((current) =>
      bringIntoView(current, rectOf(element), {
        width: node?.clientWidth ?? 0,
        height: node?.clientHeight ?? 0,
      }),
    )
    // Deliberately keyed by the element rather than by the board's contents: the arrival is the
    // event, and a card moved afterwards must not pull the view back to it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marked])

  /** Everything that moves when [element] does: the selection it belongs to, and what frames carry. */
  const movingWith = (element: BoardElement): Map<string, Place> => {
    const chosen = selection.has(element.id) ? [...selection] : [element.id]
    const taken = new Set(chosen)
    board.elements
      .filter((one) => one.kind === 'frame' && taken.has(one.id))
      .forEach((frame) => {
        board.elements
          .filter((one) => one.id !== frame.id && holds(rectOf(frame), rectOf(one)))
          .forEach((inside) => {
            taken.add(inside.id)
          })
      })
    return new Map(
      board.elements.filter((one) => taken.has(one.id)).map((one) => [one.id, placeOf(one)]),
    )
  }

  /**
   * Begins a line here.
   *
   * **Both ends are the same thing, and either of them may be nothing at all.** An end is an element
   * and an anchor, or a bare point on the surface — so a line can begin in the empty middle of a
   * board and end there too, which is what a label with a leader is, and what an arrow meaning "over
   * there" is.
   *
   * It is **one drag and nothing else**: press, move, let go. There was a second way for a while —
   * tap the source, tap the target — and it earned its keep only because a press on the bare surface
   * was being swallowed and dragging therefore did not work at all. Two ways of doing one thing, one
   * of which nobody asked for, is worse than one.
   */
  const startLine = (end: End, at: Point, event: ReactPointerEvent) => {
    dragging.current = false
    gesture.current = { kind: 'connect', pointer: event.pointerId, from: end, at }
    setDrawing({ from: at, to: at })
  }

  /** Begins a line at [element], fastened at [anchor] — or wherever it fastens itself. */
  const startLineAt = (element: BoardElement, anchor: Anchor, event: ReactPointerEvent) => {
    startLine({ element: element.id, anchor }, anchorPointOf(element, anchor), event)
  }

  /**
   * Holds on to the pointer for the rest of the gesture, where the browser offers that at all.
   *
   * It is an improvement and not a requirement: with it a drag goes on being delivered here after the
   * pointer has left the pane, and without it the gesture simply ends at the edge. Asked for rather
   * than assumed, because it is missing in jsdom — and a surface that threw there would be a surface
   * no test could drive.
   */
  const capture = (pointer: number) => {
    const node = pane.current
    if (typeof node?.setPointerCapture === 'function') {
      node.setPointerCapture(pointer)
    }
  }

  /** Where a point on the board is on the screen right now — for measuring a drag against a start. */
  const toScreenOf = (point: Point): Point => toScreen(viewport, point)

  /** Where a line let go of at [point] ends: on something, at one of its anchors, or nowhere. */
  const endAt = (point: Point): End => {
    const target = elementAt(point, placed)
    if (target === undefined) {
      return { x: point.x, y: point.y }
    }
    return {
      element: target.id,
      anchor: anchorAt(rectOf(target), point, ANCHOR_WITHIN / viewport.scale),
    }
  }

  const startDrag = (element: BoardElement, event: ReactPointerEvent) => {
    if (!rearrangeable) {
      return
    }
    dragging.current = false
    pressed.current = { on: element.id, pointerType: event.pointerType }
    gesture.current = {
      kind: 'move',
      pointer: event.pointerId,
      from: at(event),
      original: movingWith(element),
    }
  }

  const startResize = (element: BoardElement, corner: Corner, event: ReactPointerEvent) => {
    dragging.current = false
    gesture.current = {
      kind: 'resize',
      pointer: event.pointerId,
      from: at(event),
      corner,
      id: element.id,
      original: placeOf(element),
    }
  }

  const onPointerDown = (event: ReactPointerEvent) => {
    pointers.current.set(event.pointerId, at(event))
    if (pointers.current.size === 2) {
      // Two fingers: pinch takes over, and whatever the first one had begun is abandoned rather than
      // finished halfway.
      gesture.current = null
      setMoving(null)
      pinch.current = pinchOf(pointers.current)
      return
    }
    if (event.button !== 0 || !onTheBareSurface(event)) {
      return
    }
    // The empty surface. Letting go of it clears the selection; dragging it moves the board.
    onSelectLine(null)
    onSelection(new Set())

    if (connecting) {
      /*
       * A line begins on the bare surface just as readily as on a card, and ends there just as
       * readily. Nothing here asks whether there is something under the pointer — an end that hangs
       * free *is* an end, and a line between two of them is a mark somebody drew on their board.
       *
       * It also means the surface does not pan while connecting. That is the right way round: the
       * mode was asked for, it lasts until it is switched off, and the wheel still moves the board.
       */
      const point = toBoard(viewport, at(event))
      startLine({ x: point.x, y: point.y }, point, event)
      return
    }
    // The default action of a press on a surface is to begin selecting text, and a pan would then
    // drag a selection across the whole page with it. Said here as well as in the stylesheet: the
    // rule stops the selection *inside* the board, this stops it starting at all.
    event.preventDefault()
    dragging.current = false
    gesture.current = { kind: 'pan', pointer: event.pointerId, from: at(event), viewport }
  }

  const onPointerMove = (event: ReactPointerEvent) => {
    const here = at(event)
    if (pointers.current.has(event.pointerId)) {
      pointers.current.set(event.pointerId, here)
    }

    if (pointers.current.size >= 2 && pinch.current !== null) {
      const now = pinchOf(pointers.current)
      if (now !== null && pinch.current.distance > 0) {
        const factor = now.distance / pinch.current.distance
        setViewport((current) => zoomAround(current, now.middle, factor))
        pinch.current = now
      }
      return
    }

    const doing = gesture.current
    if (doing !== null && doing.kind === 'connect') {
      // It follows whichever pointer is over the board, because between two taps it belongs to none.
      // Its *own* pointer moving is what turns it into a drag, and that is finished on release.
      if (doing.pointer === event.pointerId && !dragging.current) {
        if (
          Math.hypot(here.x - toScreenOf(doing.at).x, here.y - toScreenOf(doing.at).y) >=
          DRAG_BEGINS_AT
        ) {
          dragging.current = true
          capture(event.pointerId)
        }
      }
      const point = toBoard(viewport, here)
      setDrawing({ from: doing.at, to: point })
      setAimedAt(endAt(point))
      return
    }
    if (doing === null || doing.pointer !== event.pointerId) {
      return
    }

    if (!dragging.current) {
      if (Math.hypot(here.x - doing.from.x, here.y - doing.from.y) < DRAG_BEGINS_AT) {
        return
      }
      dragging.current = true
      // Now, and not a moment earlier: from here on the pointer may leave the pane and the gesture
      // still has to follow it.
      capture(event.pointerId)
      if (doing.kind === 'waypoint' && doing.handle.kind === 'end') {
        setAiming(true)
      }
    }

    switch (doing.kind) {
      case 'pan': {
        setViewport(pannedBy(doing.viewport, here.x - doing.from.x, here.y - doing.from.y))
        break
      }
      case 'move': {
        const dx = (here.x - doing.from.x) / viewport.scale
        const dy = (here.y - doing.from.y) / viewport.scale
        setMoving(
          new Map(
            [...doing.original].map(([id, place]) => [
              id,
              { ...place, x: place.x + dx, y: place.y + dy },
            ]),
          ),
        )
        break
      }
      case 'resize': {
        const dx = (here.x - doing.from.x) / viewport.scale
        const dy = (here.y - doing.from.y) / viewport.scale
        setMoving(new Map([[doing.id, resized(doing.original, doing.corner, dx, dy)]]))
        break
      }
      case 'waypoint': {
        if (doing.handle.kind === 'end') {
          // An end being taken somewhere else. What follows the pointer is that end, so the line is
          // drawn from the *other* one — which is exactly what drawing a new line looks like.
          const ends = endsOf(doing.line, elements)
          const anchor = doing.handle.which === 'from' ? ends?.to : ends?.from
          const point = toBoard(viewport, here)
          if (anchor !== undefined) {
            setDrawing({ from: anchor, to: point })
          }
          setAimedAt(endAt(point))
          break
        }
        // Moved rather than removed while the drag lasts, whatever it is heading for: a corner that
        // vanished under the pointer and came back when it moved on would be a line that flickers.
        setBending({ id: doing.line.id, waypoints: bentTo(doing, toBoard(viewport, here)) })
        break
      }
    }
  }

  const onPointerUp = (event: ReactPointerEvent) => {
    pointers.current.delete(event.pointerId)
    if (pointers.current.size < 2) {
      pinch.current = null
    }

    const doing = gesture.current
    if (doing === null || doing.pointer !== event.pointerId) {
      return
    }
    if (!dragging.current) {
      /*
       * A touch press that never moved: a tap. **A second tap on the same thing opens it** — words
       * for writing, which is the only pair a finger can give.
       *
       * Decided here because this is the only place that knows a touch press stayed a press. Mouse
       * presses must wait for `dblclick`: opening during their second `pointerup` replaces the event
       * target before the browser has finished dispatching the click pair.
       */
      const press = pressed.current
      if (
        doing.kind === 'move' &&
        press !== null &&
        (press.pointerType === 'touch' || press.pointerType === 'pen')
      ) {
        const now = event.timeStamp
        const before = tapped.current
        const again = before?.on === press.on && now - before.at <= TAPS_PAIR_WITHIN
        tapped.current = { on: press.on, at: now }
        if (again) {
          openWriting(press.on)
        }
      }
      pressed.current = null
      gesture.current = null
      setAiming(false)
      setAimedAt(null)
      if (doing.kind === 'connect') {
        setDrawing(null)
      }
      return
    }
    pressed.current = null
    dragging.current = false

    if (doing.kind === 'connect') {
      // Let go of: on something, at one of its anchors, or on the bare surface. All three are
      // ordinary ends, and which one it is was decided by where the pointer came up.
      onConnected(doing.from, endAt(toBoard(viewport, at(event))))
      gesture.current = null
      setDrawing(null)
      setAimedAt(null)
      return
    }

    if ((doing.kind === 'move' || doing.kind === 'resize') && moving !== null && moving.size > 0) {
      // Where things were goes with where they are now, so that taking the move back is one more
      // call of the same shape rather than a second history kept somewhere.
      const before =
        doing.kind === 'move'
          ? [...doing.original].map(([id, place]) => ({ id, place }))
          : [{ id: doing.id, place: doing.original }]
      onMoved(
        [...moving].map(([id, place]) => ({ id, place })),
        before,
      )
      // Kept on the screen until the board answers with the same places. See `settling`.
      setSettling(board)
      gesture.current = null
      return
    }

    if (doing.kind === 'waypoint' && doing.handle.kind === 'end') {
      // Wherever it was let go of: on something, at one of its anchors, or on the bare surface.
      onEndMoved(doing.line, doing.handle.which, endAt(toBoard(viewport, at(event))))
      gesture.current = null
      setDrawing(null)
      setAiming(false)
      setAimedAt(null)
      return
    }

    if (doing.kind === 'waypoint') {
      onLineChanged(doing.line, letGoOf(doing, toBoard(viewport, at(event))))
      // Held, exactly as a moved card is, so the line does not spring back to its old corners for a
      // frame while the service is being told about the new ones.
      setSettling(board)
      gesture.current = null
      return
    }

    gesture.current = null
    setMoving(null)
  }

  /**
   * The wheel: scrolling moves the board, the modifier key zooms, and Shift turns the wheel sideways.
   *
   * **A native listener, and deliberately not React's `onWheel`.** React attaches wheel handlers
   * *passively*, which means `preventDefault` is ignored — so the browser went on doing its own thing
   * with the same gesture, and holding the modifier key zoomed the whole application, navigation and
   * all, while the board zoomed underneath it. A listener registered with `passive: false` is the
   * only way to say that this surface has already answered the gesture.
   *
   * A trackpad pinch arrives as a wheel event with `ctrlKey` set whichever key is actually held, so
   * pinching and modifier-scrolling land on the same branch and neither had to be invented.
   */
  useEffect(() => {
    const node = pane.current
    if (node === null) {
      return
    }
    const onWheel = (event: WheelEvent) => {
      // Before anything is decided: whatever this gesture means, it means it *here*.
      event.preventDefault()
      const where = at(event)
      if (event.ctrlKey || event.metaKey) {
        setViewport((current) => zoomAround(current, where, Math.exp(-event.deltaY / 200)))
        return
      }
      // Shift is "sideways" on every platform, but only some browsers move the value into `deltaX`
      // themselves. Swapping only when they have not is what makes it work the same everywhere
      // instead of twice.
      const sideways = event.shiftKey && event.deltaX === 0
      const dx = sideways ? event.deltaY : event.deltaX
      const dy = sideways ? 0 : event.deltaY
      setViewport((current) => pannedBy(current, -dx, -dy))
    }
    node.addEventListener('wheel', onWheel, { passive: false })
    return () => {
      node.removeEventListener('wheel', onWheel)
    }
  }, [at])

  /** Where a line's corners are while one of them is being dragged. */
  const bentTo = (doing: { line: BoardConnection; handle: Handle }, at: Point): Point[] => {
    if (doing.handle.kind === 'leg') {
      return waypointsWith(doing.line.waypoints, doing.handle.leg, at, doing.line.routing)
    }
    if (doing.handle.kind === 'corner') {
      return waypointsMoved(doing.line.waypoints, doing.handle.index, at)
    }
    // An end is being taken somewhere else; the corners stay exactly where they were put.
    return [...doing.line.waypoints]
  }

  /**
   * And where they are once it has been let go of — which is the one moment a corner can disappear.
   *
   * A corner dropped back onto the stretch between its two neighbours is taken out: it bends nothing,
   * so it is not a corner. The distance is decided in screen pixels and converted here, because what
   * matters is how close it looked to whoever let go of it, not how close it is on a board they may
   * be looking at from half as far away.
   */
  const letGoOf = (doing: { line: BoardConnection; handle: Handle }, at: Point): Point[] => {
    if (doing.handle.kind === 'leg') {
      return waypointsWith(doing.line.waypoints, doing.handle.leg, at, doing.line.routing)
    }
    if (doing.handle.kind === 'end') {
      return [...doing.line.waypoints]
    }
    const ends = endsOf(doing.line, elements)
    if (ends === null) {
      return waypointsMoved(doing.line.waypoints, doing.handle.index, at)
    }
    return waypointsAfterDragging(
      doing.line.waypoints,
      doing.handle.index,
      at,
      ends,
      REMOVED_WITHIN / viewport.scale,
    )
  }

  const select = (element: BoardElement, adding: boolean) => {
    onSelectLine(null)
    const wanted = new Set(adding ? selection : [])
    if (adding && wanted.has(element.id)) {
      wanted.delete(element.id)
    } else {
      wanted.add(element.id)
    }
    onSelection(wanted)
  }

  return (
    <div
      className={connecting ? 'board board--connecting' : 'board'}
      ref={pane}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      /*
       * The right-click menu. What it offers is decided by the board above, because these are the
       * same actions the panel beside it carries — this is a second way to reach them and never the
       * only one.
       */
      onContextMenu={(event) => {
        if (!rearrangeable) {
          return
        }
        event.preventDefault()
        const where = at(event)
        const on = elementAt(toBoard(viewport, where), placed) ?? null
        if (on !== null && !selection.has(on.id)) {
          onSelectLine(null)
          onSelection(new Set([on.id]))
        }
        setMenu({ at: where, on })
      }}
    >
      <div
        className="board__layer"
        style={
          {
            transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.scale})`,
            // What anything that must keep its size on the screen scales itself back by. Set once,
            // here, because everything below is drawn in board coordinates and shrinks with them.
            '--counter': String(1 / viewport.scale),
          } as CSSProperties
        }
      >
        <Connections
          connections={
            bending === null
              ? board.connections
              : board.connections.map((line) =>
                  line.id === bending.id ? { ...line, waypoints: bending.waypoints } : line,
                )
          }
          elements={elements}
          selected={selectedLine}
          rearrangeable={rearrangeable}
          scale={viewport.scale}
          onSelect={(line) => {
            onSelection(new Set())
            onSelectLine(line.id)
          }}
          onRemoveCorner={(line, index) => {
            onLineChanged(line, waypointsWithout(line.waypoints, index))
          }}
          onGrab={(line, handle, event) => {
            // No capture yet, and no bend yet: a press is not a drag until it has moved, which is
            // also what lets a plain tap on a handle do nothing rather than lay a corner down.
            dragging.current = false
            gesture.current = {
              kind: 'waypoint',
              pointer: event.pointerId,
              from: at(event),
              line,
              handle,
            }
          }}
          drawing={drawing}
        />

        {placed.map((element) => (
          <Element
            key={element.id}
            element={element}
            selected={selection.has(element.id)}
            marked={marked === element.id}
            rearrangeable={rearrangeable}
            connecting={connecting}
            aiming={aiming}
            aimedAt={aimedAt?.element === element.id ? (aimedAt.anchor ?? 'auto') : null}
            onSelect={select}
            onStartLine={startLineAt}
            onOpen={onOpen}
            onDragStart={startDrag}
            onResizeStart={startResize}
            onWrite={onWrite}
            onMarkup={onMarkup}
            onDisplayPage={onDisplayPage}
            onDisplayMode={onDisplayMode}
            writing={writing === element.id}
            onWriting={(open) => {
              setWriting(open ? element.id : null)
            }}
          />
        ))}
      </div>

      {menu !== null && (
        <Menu
          at={menu.at}
          entries={onMenu(menu.on)}
          onClose={() => {
            setMenu(null)
          }}
        />
      )}
    </div>
  )
}

/**
 * Whether a press landed on the bare surface rather than on something lying on it.
 *
 * **Not `target === currentTarget`.** The transformed layer covers the whole pane, so a press on the
 * empty board lands on *that* — and the stricter test threw every one of them away. Dragging the
 * surface to move the board, and dragging a line out of nowhere, both simply did nothing.
 */
function onTheBareSurface(event: ReactPointerEvent): boolean {
  if (event.target === event.currentTarget) {
    return true
  }
  const node = event.target as { classList?: DOMTokenList }
  return node.classList?.contains('board__layer') === true
}

/**
 * Every rectangle on a board — **the lines included**.
 *
 * It used to be the elements alone, so "fit everything in" quietly meant "fit the cards in": a line
 * running well past them, or one with both ends loose out in the open, was simply left off the
 * screen. A line is drawing somebody put there, and a control that says *everything* has to mean it.
 *
 * A line contributes the box around the whole path it is drawn along, corners and all, so an elbow
 * that swings wide is counted where it actually goes rather than where its ends are.
 */
function everythingOn(board: OpenBoard): Rect[] {
  const elements = new Map(board.elements.map((element) => [element.id, element]))
  const lines = board.connections.flatMap((line) => {
    const ends = endsOf(line, elements)
    if (ends === null) {
      return []
    }
    const corners = cornersOf(
      ends.from,
      ends.to,
      line.routing,
      line.waypoints,
      ends.fromSide,
      ends.toSide,
    )
    const around = boundsOf(corners.map((at) => ({ x: at.x, y: at.y, width: 0, height: 0 })))
    return around === null ? [] : [around]
  })
  return [...board.elements.map(rectOf), ...lines]
}

/** The element as it is being drawn right now: what the service said, or where a drag has it. */
function withPlace(element: BoardElement, place: Place | undefined): BoardElement {
  return place === undefined ? element : { ...element, ...place, z: place.z ?? element.z }
}

function placeOf(element: BoardElement): Place {
  return {
    x: element.x,
    y: element.y,
    width: element.width,
    height: element.height,
    z: element.z,
  }
}

/** Where on an element a line starts, from the anchor it was taken hold of at. */
function anchorPointOf(element: BoardElement, anchor: Anchor): Point {
  // Aimed at the element's own middle, which is what `auto` needs when there is no other end yet.
  const middle = { x: element.x + element.width / 2, y: element.y + element.height / 2 }
  return anchor === 'auto' ? middle : anchorPoint(rectOf(element), anchor, middle)
}

/**
 * A rectangle after a corner has been dragged.
 *
 * The two opposite edges stay put and the dragged ones move, with a floor under the width and the
 * height: the schema refuses a rectangle of zero size, and a card dragged inside out is nothing
 * anybody meant.
 */
function resized(place: Place, corner: Corner, dx: number, dy: number): Place {
  const west = corner === 'nw' || corner === 'sw'
  const north = corner === 'nw' || corner === 'ne'
  const width = Math.max(SMALLEST, west ? place.width - dx : place.width + dx)
  const height = Math.max(SMALLEST, north ? place.height - dy : place.height + dy)
  return {
    ...place,
    x: west ? place.x + (place.width - width) : place.x,
    y: north ? place.y + (place.height - height) : place.y,
    width,
    height,
  }
}

/** The smallest anything on a board may be. Below this it is a speck nobody can take hold of again. */
export const SMALLEST = 48

/**
 * How far a pointer has to move before a press becomes a drag.
 *
 * Far enough that a finger, which never presses perfectly still, still produces a click; short
 * enough that a deliberate move is never mistaken for one.
 */
const DRAG_BEGINS_AT = 4

/**
 * How long two taps on the same thing still count as a pair.
 *
 * Longer than the browser's own double click, deliberately: this pair is also the touch gesture, and
 * a finger tapping twice on an iPad is slower than a mouse doing it. Nothing is lost by being
 * generous without making two unrelated taps into a pair.
 */
const TAPS_PAIR_WITHIN = 700

function pinchOf(pointers: ReadonlyMap<number, Point>): { distance: number; middle: Point } | null {
  const [one, other] = [...pointers.values()]
  if (one === undefined || other === undefined) {
    return null
  }
  return {
    distance: Math.hypot(other.x - one.x, other.y - one.y),
    middle: { x: (one.x + other.x) / 2, y: (one.y + other.y) / 2 },
  }
}

/**
 * The viewport this board was left at.
 *
 * In `localStorage` and inside a `try` on every access, exactly as the editor's remembered choice is:
 * a browser with storage switched off, or a private window that refuses it, must cost the view it
 * was left at and never the board.
 */
function remembered(board: string): Viewport {
  try {
    const kept = window.localStorage.getItem(`${VIEWPORT_KEY}.${board}`)
    if (kept === null) {
      return AT_REST
    }
    const read = JSON.parse(kept) as Partial<Viewport>
    if (
      typeof read.x !== 'number' ||
      typeof read.y !== 'number' ||
      typeof read.scale !== 'number'
    ) {
      return AT_REST
    }
    return { x: read.x, y: read.y, scale: clampScale(read.scale) }
  } catch {
    return AT_REST
  }
}

function remember(board: string, viewport: Viewport): void {
  try {
    window.localStorage.setItem(`${VIEWPORT_KEY}.${board}`, JSON.stringify(viewport))
  } catch {
    // See above.
  }
}
